"use client";

import { Fragment, type MouseEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { getLocalizedPath, saveLocalePreference } from '@/utils/utils';
import { Transition } from '@headlessui/react';
import { Check, ChevronDown, Globe2 } from 'lucide-react';
import { languages } from '../navigation';

const localizedHomepageLocales = new Set([
    'en', 'ja', 'ko', 'de', 'es', 'fr', 'pl', 'nl', 'pt', 'ru', 'zh', 'zh-tw',
]);

const LanguageSwitcher = ({ locale }: { locale: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [pendingLocale, setPendingLocale] = useState<string | null>(null);
    const searchParams = useSearchParams();
    const pathname = usePathname();
    const switcherRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuId = useId();

    useEffect(() => {
        if (!isOpen) return;

        const handlePointerDown = (event: PointerEvent) => {
            if (!switcherRef.current?.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                triggerRef.current?.focus();
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // 移除语言前缀的函数
    const removeLanguagePrefix = (path: string): string => {
        const segments = path.split('/').filter(Boolean);

        // 如果第一个段是语言代码，移除它
        if (segments.length > 0 && languages.some(lang => lang.locale === segments[0])) {
            segments.shift();
        }

        // 返回路径，确保以 / 开头
        return '/' + segments.join('/');
    };

    const displayedLocale = pendingLocale || locale;
    const currentLanguage = languages.find(lang => lang.locale === displayedLocale)?.language || 'English';
    const queryString = searchParams.toString();

    const switcherItems = useMemo(() => {
        const pathWithoutLocale = removeLanguagePrefix(pathname);
        const availableLanguages = pathWithoutLocale === '/'
            ? languages.filter((item) => localizedHomepageLocales.has(item.locale))
            : languages;
        return availableLanguages.map((item) => ({
            ...item,
            href: `${getLocalizedPath(pathWithoutLocale, item.locale)}${queryString ? `?${queryString}` : ''}`,
        }));
    }, [pathname, queryString]);

    const handleLanguageChange = (
        event: MouseEvent<HTMLAnchorElement>,
        newLocale: string,
    ) => {
        if (newLocale === locale) {
            event.preventDefault();
            setIsOpen(false);
            return;
        }

        setPendingLocale(newLocale);
        saveLocalePreference(newLocale);
        setIsOpen(false);
    };

    return (
        <div ref={switcherRef} className="relative inline-block text-left [font-family:var(--font-body)]">
            <button
                ref={triggerRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? menuId : undefined}
                aria-busy={Boolean(pendingLocale)}
                onClick={() => setIsOpen((open) => !open)}
                className="group inline-flex min-h-11 items-center justify-center whitespace-nowrap rounded-[var(--radius-input)] border border-[var(--color-rule)] bg-[var(--color-paper-raised)] px-4 py-2 text-sm font-semibold text-[var(--color-ink-2)] transition-colors duration-150 hover:border-[var(--color-rule-2)] hover:text-[var(--color-accent-deep)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)] active:bg-[var(--color-paper-2)]"
            >
                <Globe2 className="mr-2 h-4 w-4 text-[var(--color-accent)] transition-colors duration-150 group-hover:text-[var(--color-accent-deep)]" aria-hidden="true" />
                <span>{currentLanguage}</span>
                <ChevronDown
                    className={`ml-2 h-4 w-4 text-[var(--color-muted)] transition-transform duration-200 group-hover:text-[var(--color-accent-deep)] motion-reduce:transition-none ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                />
            </button>

            <Transition
                show={isOpen}
                as={Fragment}
                enter="transition-opacity duration-150 ease-out motion-reduce:duration-0"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-100 ease-in motion-reduce:duration-0"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="absolute right-0 top-full z-[var(--z-dropdown)] mt-2 w-44 origin-top-right rounded-[var(--radius-card)] border border-[var(--color-rule)] bg-[var(--color-paper-raised)] shadow-[var(--shadow-menu)] focus:outline-none">
                    <div id={menuId} className="py-2" role="menu">
                        {switcherItems.map((item) => {
                            const isCurrent = locale === item.locale;
                            const isSwitching = pendingLocale === item.locale;

                            return (
                                <a
                                    key={item.locale}
                                    href={item.href}
                                    role="menuitem"
                                    hrefLang={item.locale}
                                    lang={item.code}
                                    aria-current={isCurrent ? 'page' : undefined}
                                    onClick={(event) => handleLanguageChange(event, item.locale)}
                                    className={`
                    group mx-1 flex min-h-11 w-[calc(100%-0.5rem)] items-center justify-between rounded-[var(--radius-small)] px-4 py-2 text-left text-sm transition-colors duration-150 hover:bg-[var(--color-accent-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)]
                    ${isCurrent ? 'bg-[var(--color-accent-soft)] font-semibold text-[var(--color-accent-deep)]' : 'text-[var(--color-ink-2)] hover:text-[var(--color-accent-deep)]'}
                  `}
                                >
                                    <span>{item.language}</span>
                                    {(isCurrent || isSwitching) && (
                                        <span className="text-[var(--color-accent)]">
                                            <Check className="h-4 w-4" aria-hidden="true" />
                                        </span>
                                    )}
                                </a>
                            );
                        })}
                    </div>
                </div>
            </Transition>
        </div>
    );
};

export default LanguageSwitcher;
